import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { logger } from '../../lib/logger';
import { resolveDateRange } from './auditLogConstants';

/**
 * Encapsulates all audit-log data fetching: filters list, summary cards,
 * paginated logs, and CSV/XLSX export. Returns the full state plus a couple
 * of bound action handlers for the page to wire into the UI.
 */
export function useAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 25;

  // Filter selections
  const [datePreset, setDatePreset] = useState('week');
  const [selectedAction, setSelectedAction] = useState('all');
  const [selectedResource, setSelectedResource] = useState('all');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Build common query params from the active filters
  const buildParams = useCallback(
    (extra = {}) => {
      const { start, end } = resolveDateRange(datePreset);
      const params = { ...extra };
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      if (selectedAction !== 'all') params.action = selectedAction;
      if (selectedResource !== 'all') params.resource_type = selectedResource;
      if (selectedSeverity !== 'all') params.severity = selectedSeverity;
      return params;
    },
    [datePreset, selectedAction, selectedResource, selectedSeverity]
  );

  const loadFilters = useCallback(async () => {
    try {
      const response = await api.get('/audit/filters');
      setFilters(response.data);
    } catch (error) {
      logger.error('Failed to load audit filters', error);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const response = await api.get('/audit/summary', { params: buildParams() });
      setSummary(response.data);
    } catch (error) {
      logger.error('Failed to load audit summary', error);
    }
  }, [buildParams]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams({ skip: page * pageSize, limit: pageSize });
      if (searchTerm) params.search = searchTerm;
      const response = await api.get('/audit/logs', { params });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Only Admin and Office Manager can view audit logs.');
      } else {
        toast.error('Failed to load audit logs');
      }
    } finally {
      setLoading(false);
    }
  }, [buildParams, page, pageSize, searchTerm]);

  const exportLogs = useCallback(
    async (format) => {
      try {
        const response = await api.get('/audit/export', {
          params: buildParams({ format }),
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `audit_logs.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`Audit logs exported as ${format.toUpperCase()}`);
      } catch {
        toast.error('Failed to export audit logs');
      }
    },
    [buildParams]
  );

  // Bootstrap once
  useEffect(() => {
    loadFilters();
    loadSummary();
  }, [loadFilters, loadSummary]);

  // Re-fetch logs whenever paging / filters change. Search is opt-in via Enter.
  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return {
    // Data
    logs,
    summary,
    filters,
    loading,
    total,
    page,
    pageSize,
    totalPages: Math.ceil(total / pageSize),
    // Filter setters (auto-reset page on change)
    datePreset,
    setDatePreset: (v) => {
      setDatePreset(v);
      setPage(0);
    },
    selectedAction,
    setSelectedAction: (v) => {
      setSelectedAction(v);
      setPage(0);
    },
    selectedResource,
    setSelectedResource: (v) => {
      setSelectedResource(v);
      setPage(0);
    },
    selectedSeverity,
    setSelectedSeverity: (v) => {
      setSelectedSeverity(v);
      setPage(0);
    },
    searchTerm,
    setSearchTerm,
    setPage,
    // Actions
    loadLogs,
    loadSummary,
    exportLogs,
    handleSearch: () => {
      setPage(0);
      loadLogs();
    },
    handleRefresh: () => {
      loadLogs();
      loadSummary();
    },
  };
}
